import { z } from 'zod';
import { paginatedResponse, optionalCuid } from './common';

/**
 * Schemas for /clients and the nested /clients/:id/delivery-addresses sub-resource.
 * Mirrored on client and server via @kreart/shared.
 */

// ---------- DeliveryAddress ----------

export const deliveryAddressTypeEnum = z.enum(['OFFICE', 'WAREHOUSE', 'STORE']);
export type DeliveryAddressTypeValue = z.infer<typeof deliveryAddressTypeEnum>;

export const deliveryAddress = z.object({
  id: z.string().cuid(),
  clientId: z.string().cuid(),
  label: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  addressLine3: z.string().nullable(),
  city: z.string(),
  county: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string(),
  locationCode: z.string().nullable(),
  type: deliveryAddressTypeEnum.nullable(),
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  isDefault: z.boolean(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type DeliveryAddress = z.infer<typeof deliveryAddress>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

const requiredTrim = (max: number) => z.string().trim().min(1).max(max);
const clearableTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? null : v));

export const createDeliveryAddressRequest = z.object({
  label: optionalTrim(80),
  addressLine1: optionalTrim(200),
  addressLine2: optionalTrim(200),
  addressLine3: optionalTrim(200),
  city: z.string().trim().min(1).max(120),
  county: optionalTrim(120),
  postalCode: optionalTrim(20),
  country: z.string().trim().length(2).toUpperCase().default('RO'),
  locationCode: optionalTrim(50),
  type: deliveryAddressTypeEnum.optional(),
  contactName: optionalTrim(120),
  contactEmail: z
    .string()
    .trim()
    .toLowerCase()
    .max(254)
    .email()
    .optional()
    .or(z.literal('').transform(() => undefined)),
  contactPhone: optionalTrim(40),
  isDefault: z.boolean().optional(),
  notes: optionalTrim(1000),
});
export type CreateDeliveryAddressRequest = z.infer<typeof createDeliveryAddressRequest>;

export const updateDeliveryAddressRequest = z
  .object({
    label: optionalTrim(80),
    addressLine1: optionalTrim(200),
    addressLine2: optionalTrim(200),
    addressLine3: optionalTrim(200),
    city: z.string().trim().min(1).max(120).optional(),
    county: optionalTrim(120),
    postalCode: optionalTrim(20),
    country: z.string().trim().length(2).toUpperCase().optional(),
    locationCode: optionalTrim(50),
    type: deliveryAddressTypeEnum.nullable().optional(),
    contactName: optionalTrim(120),
    contactEmail: z
      .string()
      .trim()
      .toLowerCase()
      .max(254)
      .email()
      .optional()
      .or(z.literal('').transform(() => undefined)),
    contactPhone: optionalTrim(40),
    isDefault: z.boolean().optional(),
    notes: optionalTrim(1000),
  })
  .refine(
    (v) =>
      v.label !== undefined ||
      v.addressLine1 !== undefined ||
      v.addressLine2 !== undefined ||
      v.addressLine3 !== undefined ||
      v.city !== undefined ||
      v.county !== undefined ||
      v.postalCode !== undefined ||
      v.country !== undefined ||
      v.locationCode !== undefined ||
      v.type !== undefined ||
      v.contactName !== undefined ||
      v.contactEmail !== undefined ||
      v.contactPhone !== undefined ||
      v.isDefault !== undefined ||
      v.notes !== undefined,
    { message: 'At least one field is required.' },
  );
export type UpdateDeliveryAddressRequest = z.infer<typeof updateDeliveryAddressRequest>;

// ---------- Client ----------

export const clientListItem = z.object({
  id: z.string().cuid(),
  name: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  company: z.string().nullable(),
  brand: z.string().nullable(),
  codClient: z.string().nullable(),
  contactName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  taxId: z.string().nullable(),
  regCom: z.string().nullable(),
  vatPayer: z.boolean(),
  iban: z.string().nullable(),
  bank: z.string().nullable(),
  billingStreet: z.string().nullable(),
  billingCity: z.string().nullable(),
  billingCounty: z.string().nullable(),
  billingCountry: z.string().nullable(),
  city: z.string().nullable(),
  ownerId: z.string().cuid().nullable(),
  deliveryAddressCount: z.number().int().nonnegative(),
  projectCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
});
export type ClientListItem = z.infer<typeof clientListItem>;

export const clientDetails = clientListItem
  .omit({ deliveryAddressCount: true, projectCount: true })
  .extend({
    notes: z.string().nullable(),
    updatedAt: z.string().datetime(),
    deliveryAddresses: z.array(deliveryAddress),
  });
export type ClientDetails = z.infer<typeof clientDetails>;

export const listClientsQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(100).optional(),
  includeDeleted: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'string' ? v === 'true' : v))
    .default(false),
});
export type ListClientsQuery = z.infer<typeof listClientsQuery>;

export const listClientsResponse = paginatedResponse(clientListItem);
export type ListClientsResponse = z.infer<typeof listClientsResponse>;

export const createClientRequest = z.object({
  firstName: optionalTrim(100),
  lastName: requiredTrim(100),
  company: optionalTrim(200),
  brand: optionalTrim(100),
  codClient: optionalTrim(50),
  contactName: optionalTrim(120),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .max(254)
    .email()
    .optional()
    .or(z.literal('').transform(() => undefined)),
  phone: optionalTrim(40),
  taxId: optionalTrim(40),
  regCom: optionalTrim(50),
  vatPayer: z.boolean().default(false),
  iban: optionalTrim(50),
  bank: optionalTrim(100),
  billingStreet: optionalTrim(200),
  billingCity: optionalTrim(120),
  billingCounty: optionalTrim(120),
  billingCountry: z.string().trim().length(2).toUpperCase().default('RO').optional(),
  ownerId: optionalCuid(),
  notes: optionalTrim(2000),
});
export type CreateClientRequest = z.infer<typeof createClientRequest>;

export const updateClientRequest = z
  .object({
    firstName: clearableTrim(100),
    lastName: requiredTrim(100).optional(),
    company: optionalTrim(200),
    brand: optionalTrim(100),
    codClient: optionalTrim(50),
    contactName: optionalTrim(120),
    email: z
      .string()
      .trim()
      .toLowerCase()
      .max(254)
      .email()
      .optional()
      .or(z.literal('').transform(() => undefined)),
    phone: optionalTrim(40),
    taxId: optionalTrim(40),
    regCom: optionalTrim(50),
    vatPayer: z.boolean().optional(),
    iban: optionalTrim(50),
    bank: optionalTrim(100),
    billingStreet: optionalTrim(200),
    billingCity: optionalTrim(120),
    billingCounty: optionalTrim(120),
    billingCountry: z.string().trim().length(2).toUpperCase().optional(),
    ownerId: optionalCuid().or(z.null()),
    notes: optionalTrim(2000),
  })
  .refine(
    (v) =>
      v.firstName !== undefined ||
      v.lastName !== undefined ||
      v.company !== undefined ||
      v.brand !== undefined ||
      v.codClient !== undefined ||
      v.contactName !== undefined ||
      v.email !== undefined ||
      v.phone !== undefined ||
      v.taxId !== undefined ||
      v.regCom !== undefined ||
      v.vatPayer !== undefined ||
      v.iban !== undefined ||
      v.bank !== undefined ||
      v.billingStreet !== undefined ||
      v.billingCity !== undefined ||
      v.billingCounty !== undefined ||
      v.billingCountry !== undefined ||
      v.ownerId !== undefined ||
      v.notes !== undefined,
    { message: 'At least one field is required.' },
  );
export type UpdateClientRequest = z.infer<typeof updateClientRequest>;
